import {
  Box,
  Button,
  ClickAwayListener,
  Icon,
  Popper,
  SxProps,
} from "@outschool/backpack";
import { faChevronDown, faChevronRight } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { TrackedButton } from "@outschool/ui-components-shared";
import { useEventListener, useNavigation } from "@outschool/ui-utils";
import React, { useCallback, useRef, useState } from "react";

import { CategoryPartial } from "./navbarCategories";
import {
  buildCategoryTrackingProperties,
  CATEGORY_NAVBAR_COMPONENT,
  trackSubmenuOpen,
} from "./categoryNavbarTracking";
import { getCategoryUrl } from "./categoryNavbarUtils";

export default function DesktopCategoryNavbarButton({
  categories,
  sx = {},
}: {
  categories: CategoryPartial[];
  sx?: SxProps;
}) {
  const { t } = useTranslation(
    "ui-components-website\\CategoryNavbar\\DesktopCategoryNavbarButton",
  );
  const trackEvent = useTrackEvent();
  const [selectedCategory, setSelectedCategory] = useState<
    CategoryPartial | undefined
  >();

  const [selectedSubCategory, setSelectedSubCategory] = useState<
    CategoryPartial | undefined
  >();

  const [anchorEl, setAnchorEl] = React.useState<
    HTMLButtonElement | undefined
  >();

  const document = (
    typeof window !== "undefined" ? window.document : null
  ) as Document | null;

  const deselect = useCallback(() => {
    setAnchorEl(undefined);
    setSelectedCategory(undefined);
    setSelectedSubCategory(undefined);
  }, []);

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        deselect();
      }
    },
    [deselect],
  );
  useEventListener(document, "keyup", onEscape);

  const navigate = useNavigation();

  const handleClick = React.useCallback(
    (category: CategoryPartial) => {
      deselect();
      navigate(getCategoryUrl(category), { hardNav: category.reloadDocument });
    },
    [navigate, deselect],
  );

  const handleBrowseOpen = useCallback(
    (wasAlreadyOpen: boolean) => {
      if (!wasAlreadyOpen) {
        trackEvent("category_navbar_browse_open");
      }
    },
    [trackEvent],
  );

  return (
    <Box sx={sx}>
      <Button
        ref={anchorEl}
        variant="text"
        color="primary"
        endIcon={<Icon icon={faChevronDown} />}
        onClick={(event: React.MouseEvent) => {
          const wasOpen = Boolean(anchorEl);
          setAnchorEl(event.currentTarget as any);
          handleBrowseOpen(wasOpen);
        }}
        onMouseOver={(event: React.MouseEvent) => {
          const wasOpen = Boolean(anchorEl);
          setAnchorEl(event.currentTarget as any);
          handleBrowseOpen(wasOpen);
        }}
        sx={{
          "&:hover:not(:disabled), &:active:not(:disabled), &:focus:not(:disabled)":
            {
              backgroundColor: "transparent",
            },
        }}
      >
        {t("Browse")}
      </Button>
      <Popper
        anchorEl={anchorEl}
        placement="bottom-start"
        open={Boolean(anchorEl)}
        useArrow={false}
        sx={{
          padding: 0,
          // Trying to match placement of search suggestions menu
          marginTop: 13,
          borderRadius: 8,
          border: "none",
        }}
      >
        <ClickAwayListener
          onClickAway={() => {
            deselect();
          }}
        >
          <Box flex>
            <Box sx={{ padding: 8 }}>
              {categories.map(category => (
                <SingleCategoryItem
                  key={category.slug}
                  category={category}
                  selected={category === selectedCategory}
                  onMouseoverItem={() => {
                    trackSubmenuOpen(trackEvent, category, {
                      wasAlreadySelected: category === selectedCategory,
                      component: CATEGORY_NAVBAR_COMPONENT.BROWSE_MENU,
                    });
                    setSelectedCategory(category);
                    setSelectedSubCategory(undefined);
                  }}
                  trackingName="category-navbar-desktop-item"
                  trackingUniqueId={category.slug}
                  trackingSharedProperties={buildCategoryTrackingProperties(
                    category,
                    { component: CATEGORY_NAVBAR_COMPONENT.BROWSE_MENU },
                  )}
                  trackViews={true}
                  onClick={handleClick}
                />
              ))}
            </Box>
            {selectedCategory && selectedCategory.subcategories && (
              <Box sx={{ padding: 8 }}>
                {selectedCategory.subcategories?.map(category => (
                  <SingleCategoryItem
                    key={category.slug}
                    category={category}
                    selected={category === selectedSubCategory}
                    onMouseoverItem={() => {
                      trackSubmenuOpen(trackEvent, category, {
                        wasAlreadySelected: category === selectedSubCategory,
                        component: CATEGORY_NAVBAR_COMPONENT.BROWSE_MENU,
                        ancestors: [selectedCategory],
                      });
                      setSelectedSubCategory(category);
                    }}
                    trackingName="category-navbar-desktop-popup-item"
                    trackingUniqueId={`${selectedCategory.slug}/${category.slug}`}
                    trackingSharedProperties={buildCategoryTrackingProperties(
                      category,
                      {
                        component: CATEGORY_NAVBAR_COMPONENT.BROWSE_MENU,
                        ancestors: [selectedCategory],
                      },
                    )}
                    trackViews={false}
                    disabled={category.can_link === false}
                    onClick={handleClick}
                  />
                ))}
              </Box>
            )}
            {selectedSubCategory && selectedSubCategory.subcategories && (
              <Box sx={{ padding: 8 }}>
                {selectedSubCategory.subcategories?.map(category => (
                  <SingleCategoryItem
                    key={category.slug}
                    category={category}
                    trackingName="category-navbar-desktop-popup-item"
                    trackingUniqueId={`${selectedSubCategory.slug}/${category.slug}`}
                    trackingSharedProperties={buildCategoryTrackingProperties(
                      category,
                      {
                        component: CATEGORY_NAVBAR_COMPONENT.BROWSE_MENU,
                        ancestors: [
                          selectedSubCategory,
                          selectedCategory,
                        ].filter(Boolean) as CategoryPartial[],
                      },
                    )}
                    trackViews={false}
                    onClick={handleClick}
                  />
                ))}
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}

function SingleCategoryItem({
  category,
  onMouseoverItem,
  selected,
  disabled = false,
  trackingName,
  trackingUniqueId,
  trackingSharedProperties,
  trackViews,
  onClick,
}: {
  category: CategoryPartial;
  trackingName: string;
  trackingUniqueId: string;
  trackingSharedProperties?: Record<string, any>;
  trackViews: boolean;
  onMouseoverItem?: () => void;
  selected?: boolean;
  disabled?: boolean;
  onClick: (category: CategoryPartial) => void;
}) {
  const buttonRef = useRef<HTMLElement>();

  return (
    <TrackedButton
      variant="text"
      ref={buttonRef as any}
      trackingName={trackingName}
      trackingUniqueId={trackingUniqueId}
      trackingSharedProperties={trackingSharedProperties}
      trackViews={trackViews}
      color="inherit"
      endIcon={
        category.subcategories &&
        category.subcategories.length > 0 && (
          <Icon
            icon={faChevronRight}
            sx={{
              marginLeft: 8,
              ":nth-of-type(1)": {
                // required to make icon smaller due to the way we handle icons in buttons
                fontSize: 16,
              },
            }}
          />
        )
      }
      sx={{
        minWidth: 200,
        padding: 8,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        ":hover": {
          backgroundColor: "primary.50",
          color: "inherit",
        },
        color: selected ? "primary.main" : undefined,
        cursor: "pointer",
        width: "100%",
        borderRadius: 0,
      }}
      onClick={() => {
        if (disabled) {
          return;
        }
        onClick(category);
      }}
      onMouseEnter={onMouseoverItem}
    >
      {category.nameTitleCased}
    </TrackedButton>
  );
}
